import React, { useMemo, useState } from "react";
import AccountBalance from "@mui/icons-material/AccountBalance";
import CheckCircle from "@mui/icons-material/CheckCircle"; 
import CreditCard from "@mui/icons-material/CreditCard";
import Payments from "@mui/icons-material/Payments";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import LockOutlined from "@mui/icons-material/LockOutlined";
import "./AccountPayment.css";

/*
 * --------------------------------------------------------------------------
 * PAYMENT ACCOUNTS
 * --------------------------------------------------------------------------
 * These are demo/test accounts.
 *
 * In production, replace this array with the accounts returned from your
 * backend.
 */
const PAYMENT_ACCOUNTS = [
  {
    id: "zenith-001",
    type: "bank",
    bankName: "Zenith Bank",
    accountName: "Abdullahi Musa",
    accountNumber: "1012345678",
    status: "default",
    shortName: "ZB",
  },
  {
    id: "moneypoint-001",
    type: "pos",
    bankName: "MoneyPoint POS",
    accountName: "Aisha Suleiman",
    accountNumber: "MP-784215",
    status: "active",
    shortName: "MP",
  },
  {
    id: "opay-001",
    type: "pos",
    bankName: "OPay POS",
    accountName: "Ibrahim Abdullahi",
    accountNumber: "OP-592841",
    status: "active",
    shortName: "OP",
  },
  {
    id: "greenbank-001",
    type: "bank",
    bankName: "Green Bank PLC",
    accountName: "Maryam Hassan",
    accountNumber: "2034567891",
    status: "active",
    shortName: "GB",
  },
  {
    id: "gtbank-001",
    type: "bank",
    bankName: "GTBank PLC",
    accountName: "Yusuf Ibrahim",
    accountNumber: "3012456789",
    status: "active",
    shortName: "GT",
  },
  {
    id: "gtbank-ng-001",
    type: "bank",
    bankName: "GTBank Nigeria",
    accountName: "Fatima Sani",
    accountNumber: "4023456781",
    status: "active",
    shortName: "GT",
  },
  {
    id: "zenith-pos-001",
    type: "pos",
    bankName: "Zenith POS",
    accountName: "Sadiq Bello",
    accountNumber: "ZP-651902",
    status: "active",
    shortName: "ZP",
  },
];



const AccountPayment = ({
  banksAccount,
  amount = 0,
  executeAcctPay,
  setSelectedBankAccount,
  selectedBankAccount,
  salesPayload,
  isSubmitting = false,
}) => {
  
  
    /*
   * Finds the default account.
   * If the backend accidentally returns no default account, we safely
   * fall back to the first account.
   */
  const getDefaultAccount = () => {
    return (
      banksAccount?.find((account) => account.status === "default") ||
      banksAccount?.[0]
    );
  };
  
  
  const formatAmount = (amount) => {
    const numericAmount = Number(amount || 0);
  
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  };
  
  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return "";
  
    if (accountNumber.length <= 6) {
      return accountNumber;
    }
  
    const first = accountNumber.slice(0, 3);
    const last = accountNumber.slice(-4);
  
    return `${first} •••• ••• ${last}`;
  };
  
  const getAccountIcon = (type) => {
    return type === "pos" ? (
      <CreditCard className="accountPaymentTypeIcon" />
    ) : (
      <AccountBalance className="accountPaymentTypeIcon" />
    );
  };
  
  
   const defaultAccount = useMemo(() => getDefaultAccount(), []);
  
  // -----------------------------------------------------
 
  const [maskAccount, setmaskAccount] = useState(true)
  const [selectedAccountId, setSelectedAccountId] = useState(
    defaultAccount?.id
  );

  const { 
    discount,
    customerName,
    customerId, 
    setOpenModal, 
    subTotal,
    total,
    tax,
  } = salesPayload;

  const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
  const [description, setDescription] = useState("");

  const selectedAccount = useMemo(() => {
    return (
      banksAccount?.find(
        (account) => account.id === selectedAccountId
      ) || defaultAccount
    );
  }, [selectedAccountId, defaultAccount]);

 const handleAccountSelect = (account) => {
  setSelectedAccountId(account.id);
  setIsAccountSelectorOpen(false);

  setSelectedBankAccount({
    accountId: account.id,
    accountName: account.accountName,
    accountNumber: account.accountNumber,
    bankName: account.bankName,
    accountType: account.type,
    description: description.trim(),
  });
};

  const handleSubmit = async () => {
  if (!selectedAccount || isSubmitting) return;


  if (typeof executeAcctPay === "function") {
    await executeAcctPay();
  }
};

const hasBankAccounts =
  Array.isArray(banksAccount) && banksAccount?.length > 0;

  return (
    <section className="accountPaymentWrapps">
  <div
    className="accountPayment fx-cl space2 relative"
    onClick={(e) => e.stopPropagation()}
  >
    {/* ------------------------------------------------------------------
        HEADER
    ------------------------------------------------------------------ */}
    <div className="accountPaymentHeader fx-ac fx-jb">
      <div className="fx-ac space">
        <div className="accountPaymentHeaderIcon fx-ac fx-jc">
          <Payments />
        </div>

        <div className="fx-cl spacem">
          <span className="fs3 fw600 accountPaymentTitle">
            Account Payment
          </span>

          <span className="fs1 cb">
            Select where this payment should be received.
          </span>
        </div>
      </div>

      <div className="accountPaymentMethodBadge fx-ac space">
        <AccountBalance />
        <span>ACCOUNT</span>
      </div>
    </div>

    {/* ------------------------------------------------------------------
        MAIN SELECTED ACCOUNT CARD
    ------------------------------------------------------------------ */}
    <div className="accountPaymentMainCard shadowA br4 relative">
      <div className="accountPaymentMainGlow" />

      <div className="fx-ac fx-jb accountPaymentTop">
        <div className="fx-ac space1">
          <div className="accountBankLogo fx-ac fx-jc">
            {selectedAccount?.shortName}
          </div>

          <div className="fx-cl spacem">
            <span className="fs4 fw600 cw">
              {selectedAccount?.bankName}
            </span>

            <div className="fx-ac space">
              {getAccountIcon(selectedAccount?.type)}

              <span className="fs1 accountPaymentTypeText">
                {selectedAccount?.type === "pos"
                  ? "POS Account"
                  : "Bank Account"}
              </span>
            </div>
          </div>
        </div>

        <div className="accountDefaultBadge fx-ac space">
          <CheckCircle />
          <span>Active</span>
        </div>
      </div>

      <div className="accountPaymentAmountSection fx-cl">
        <span className="fs1 accountPaymentAmountLabel">
          Amount to be received
        </span>

        <span className="accountPaymentAmount fs8 fwb">
          {formatAmount(amount)}
        </span>
      </div>

      <div className="accountPaymentAccountSection fx-cl space1">
        <span className="fs1 accountPaymentAccountLabel">
          Account Number
        </span>

        <span
          className="accountPaymentAccountNumber fs6 fw600"
          style={{ cursor: "pointer" }}
          onClick={() => setmaskAccount(!maskAccount)}
        >
          {maskAccount
            ? maskAccountNumber(selectedAccount?.accountNumber)
            : selectedAccount?.accountNumber}
        </span>

        <span className="fs1 accountPaymentAccountName">
          {selectedAccount?.accountName}
        </span>
      </div>
    </div>

    {/* ------------------------------------------------------------------
        ACCOUNT SWITCHER
    ------------------------------------------------------------------ */}
    <div className="accountSelectorWrapper relative">
      <button
        type="button"
        className="accountSelectorButton fx-ac fx-jb"
        onClick={() => {
          if (!hasBankAccounts) return;

          setIsAccountSelectorOpen((current) => !current);
        }}
        aria-expanded={isAccountSelectorOpen}
        disabled={!hasBankAccounts}
      >
        <div className="fx-ac space1">
          <div className="accountSelectorMiniLogo fx-ac fx-jc">
            {selectedAccount?.shortName}
          </div>

          <div className="fx-cl spacem">
            <span className="fs1 cb">
              Receiving account
            </span>

            <span className="fs3 fw600">
              {selectedAccount?.bankName}
            </span>
          </div>
        </div>

        <div className="fx-ac space">
          <span className="fs1 cb">
            Change
          </span>

          <Payments
            className={
              isAccountSelectorOpen
                ? "accountSelectorArrow accountSelectorArrowOpen"
                : "accountSelectorArrow"
            }
          />
        </div>
      </button>

      {/* ----------------------------------------------------------------
          ACCOUNT SELECTION PANEL
      ---------------------------------------------------------------- */}
      {isAccountSelectorOpen && hasBankAccounts && (
        <div className="accountSelectorPanel shadowA br3">
          <div className="accountSelectorPanelHeader fx-cl spacem">
            <span className="fs3 fw600">
              Choose receiving account
            </span>

            <span className="fs1 cb">
              Select the account or POS terminal that should receive
              this payment.
            </span>
          </div>

          <div className="accountSelectorList fx-cl space">
            {banksAccount?.map((account) => {
              const isActive =
                account.id === selectedAccount?.id;

              return (
                <button
                  type="button"
                  key={account.id}
                  className={
                    isActive
                      ? "accountOption accountOptionActive fx-ac fx-jb"
                      : "accountOption fx-ac fx-jb"
                  }
                  onClick={() => handleAccountSelect(account)}
                >
                  <div className="fx-ac space1">
                    <div className="accountOptionLogo fx-ac fx-jc">
                      {account.shortName}
                    </div>

                    <div className="fx-cl spacem">
                      <div className="fx-ac space">
                        <span className="fs2 fw600">
                          {account.bankName}
                        </span>

                        {account.type === "pos" && (
                          <span className="accountPosLabel">
                            POS
                          </span>
                        )}
                      </div>

                      <span className="fs1 cb">
                        {account.accountName}
                      </span>

                      <span className="fs1 cb">
                        {account.accountNumber}
                      </span>
                    </div>
                  </div>

                  {isActive && (
                    <CheckCircle className="accountOptionCheck" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>

    {/* ------------------------------------------------------------------
        DESCRIPTION
    ------------------------------------------------------------------ */}
    <div className="accountPaymentDescription fx-cl space">
      <div className="fx-ac space">
        <DescriptionOutlined className="accountDescriptionIcon" />

        <label
          htmlFor="account-payment-description"
          className="fs2 fw600"
        >
          Transaction Description
        </label>

        <span className="fs1 cb">
          (Optional)
        </span>
      </div>

      <textarea
        id="account-payment-description"
        className="accountPaymentTextarea"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Enter description for this transaction, if applicable..."
        rows={3}
        maxLength={250}
      />

      <div className="fx-je">
        <span className="fs1 cb">
          {description.length}/250
        </span>
      </div>
    </div>

    {/* ------------------------------------------------------------------
        SUBMIT
    ------------------------------------------------------------------ */}
    <div className="accountPaymentSubmitSection fx-cl space">
      <button
        type="button"
        className={
          isSubmitting
            ? "accountPaymentSubmitButton accountPaymentSubmitLoading fx-ac fx-jc space1"
            : "accountPaymentSubmitButton fx-ac fx-jc space1"
        }
        disabled={
          isSubmitting ||
          !selectedAccount ||
          !hasBankAccounts
        }
        onClick={handleSubmit}
      >
        {isSubmitting ? (
          <>
            <span className="accountPaymentSpinner" />
            <span>Processing Payment...</span>
          </>
        ) : (
          <>
            <span>Submit Transaction</span>
            <ArrowForward />
          </>
        )}
      </button>
    </div>

    {/* ------------------------------------------------------------------
        FOOTER NOTE
    ------------------------------------------------------------------ */}
    <div className="accountPaymentFooter fx-ac fx-jc space">
      <LockOutlined />

      <span>
        This payment will be recorded against the selected receiving
        account. Please verify the account details and amount before
        submitting this transaction.
      </span>
    </div>
  </div>
</section>
  );
};

export default AccountPayment;